import { useCallback } from 'react';

/**
 * Options for number input handler
 */
export interface UseNumberInputOptions {
  /**
   * Callback to handle form field changes
   * @param field - Field name/key
   * @param value - New value (number, string, or undefined)
   */
  onFieldChange: (field: string, value: any) => void;
  
  /**
   * Set of validation errors (to clear errors when user types)
   */
  validationErrors?: Set<string>;
  
  /**
   * Callback to clear validation error for a field
   * @param field - Field name to clear error for
   */
  onClearValidationError?: (field: string) => void;
  
  /**
   * Optional callback to update form data directly (for intermediate states like "0.")
   * Used by Dashboard component to store string values temporarily
   * @param field - Field name
   * @param value - Value to set
   */
  onFormDataUpdate?: (field: string, value: any) => void;
}

/**
 * Hook to handle number input with validation and negative value prevention
 * Consolidates the handleNumberChange logic from Dashboard and ProductDetails components
 * 
 * @param options - Configuration options
 * @returns Handler function for number input changes
 */
export const useNumberInput = (options: UseNumberInputOptions) => {
  const {
    onFieldChange,
    validationErrors,
    onClearValidationError,
    onFormDataUpdate,
  } = options;

  const handleNumberChange = useCallback((
    field: string,
    inputValue: string,
    isInteger: boolean = false
  ) => {
    // Clear validation error when user starts typing
    if (validationErrors?.has(field) && onClearValidationError) {
      onClearValidationError(field);
    }

    // Allow empty string (for clearing the field)
    if (inputValue === '' || !inputValue) {
      onFieldChange(field, undefined);
      return;
    }

    // Reject if starts with negative sign
    if (inputValue.startsWith('-')) {
      return; // Don't update, just ignore the input
    }

    // For integer fields, only allow digits (no decimals)
    if (isInteger) {
      // Allow only digits
      if (!/^\d+$/.test(inputValue)) {
        return; // Don't update if not a valid integer
      }
      const numValue = parseInt(inputValue, 10);
      onFieldChange(field, numValue);
      return;
    }

    // For decimal fields:
    // Allow: "0", "0.", "0.5", "5", "5.", "5.5", etc.
    // Pattern: optional digits, optional dot, optional digits after dot
    // Must have at least one digit before or after the dot

    // Allow input if it matches valid decimal pattern (including intermediate states like "0." or ".")
    if (/^\d*\.?\d*$/.test(inputValue)) {
      // If it's just a dot or ends with a dot, allow it for typing but don't convert yet
      if (inputValue === '.' || inputValue.endsWith('.')) {
        // Handle intermediate decimal state (e.g., "0.", "12.")
        if (onFormDataUpdate) {
          // Store as string for display (Dashboard component approach)
          onFormDataUpdate(field, inputValue);
        } else {
          // Alternative approach: parse partial number (ProductDetails component approach)
          const partialNum = parseFloat(inputValue);
          if (!isNaN(partialNum) && partialNum >= 0) {
            onFieldChange(field, partialNum);
          } else if (inputValue === '0.') {
            onFieldChange(field, 0);
          }
        }
        return;
      }

      // If it's a valid number (not just a dot), parse and store it
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue) && numValue >= 0) {
        onFieldChange(field, numValue);
      }
    }
  }, [onFieldChange, validationErrors, onClearValidationError, onFormDataUpdate]);

  return handleNumberChange;
};
